import type { SensorTypeDto } from '@/features/admin/types/opsExtended.types';
import { sensorTypeCatalogKeyLower } from '@/features/admin/utils/sensorTypeCatalogKey';
import type { IoTDataPoint } from '@/features/realtime/types/realtime.types';

const IOT_TYPES = new Set(['water_level', 'rainfall', 'soil_moisture', 'temperature', 'humidity']);

/**
 * Map sensor_type slug from BE to IoTDataPoint.type.
 * Ma khong thuoc tap tren -> geophysical (dia vat ly / kenh tuy chinh).
 */
export function mapSensorTypeCodeToIoTType(code: string): IoTDataPoint['type'] {
  const c = code.trim().toLowerCase();

  if (IOT_TYPES.has(c)) return c as NonNullable<IoTDataPoint['type']>;

  return 'geophysical';
}

/**
 * Map catalog `/api/sensor-types` → IoT type theo slug `code` (không đoán từ tên hiển thị hay group_code).
 * Index cả UUID/id lẫn slug để khớp `sensorChannelCode` từ trạm.
 */
export function buildCatalogIoTTypeMap(
  sensorTypes: SensorTypeDto[],
): Map<string, NonNullable<IoTDataPoint['type']>> {
  const m = new Map<string, NonNullable<IoTDataPoint['type']>>();
  for (const st of sensorTypes) {
    const catalogKey = sensorTypeCatalogKeyLower(st);
    if (!catalogKey) continue;
    const slug = (typeof st.code === 'string' ? st.code.trim() : '') || catalogKey;
    const type = mapSensorTypeCodeToIoTType(slug) as NonNullable<IoTDataPoint['type']>;
    m.set(catalogKey, type);
    const slugLower = slug.toLowerCase();
    if (slugLower !== catalogKey) m.set(slugLower, type);
  }
  return m;
}

/**
 * Trả về nhãn tiếng Việt cho loại trạm (dùng hiển thị trên thẻ thông số).
 * Ưu tiên dùng tên từ BE nếu có, nếu không thì dùng fallback theo type.
 */
export function getIoTTypeLabel(type?: IoTDataPoint['type'], customName?: string): string {
  if (customName?.trim()) return customName.trim();

  switch (type) {
    case 'water_level':
      return 'Mực nước';
    case 'rainfall':
      return 'Lượng mưa';
    case 'soil_moisture':
      return 'Độ ẩm đất';
    case 'temperature':
      return 'Nhiệt độ';
    case 'humidity':
      return 'Độ ẩm';
    default:
      return 'Dữ liệu';
  }
}
